// ─────────────────────────────────────────────────────────────────────────────
// 🔑 THE DASHBOARD'S MAIN CARD, ONE PER STAGE (Jesse/Hannah, 2026-11-02 — the Home Shopping page redesign, approved).
// The guided flow's white step card (GuidedStepCard's card), its title and line (GuidedDocumentsStep's 16/800 and
// 13.5/1.6), its light-blue panel (Step5AskCard ASK_BOX) and its primary button. Every figure comes from the status read's
// `journey` (backend utils/borrowerJourney); every word from constants/journeySteps JOURNEY_COPY (approved).
//   Home Shopping  — "You’re approved up to $[live decision maximum]", today's next-step sentence, item 10's sentence in
//                    the light-blue panel ("down payment" plain text), "View your SmartEstimate", "Browse homes"
//                    (full width on a phone, normal width on a computer).
//                    🔑 "View your SmartEstimate" opens the borrower's SIGNED SmartEstimate PDF (journey
//                    .smartestimate_document_id — their own signed row, backend utils/borrowerJourney) in the Documents
//                    viewer; NEVER the simulator (Hannah's review 2026-11-03 item 3). A joint co-borrower gets the
//                    primary's signed copy (the viewer link lets that file's co-borrower open it). No signed PDF → no link.
//   Under contract — "Under contract · [address] · $[contract price]"; during the option period, "Your option period
//                    ends [Mon D] at 5:00 p.m." (5 p.m. where the home is — no zone shown).
//   Closing        — "Closing · [address] · $[price]" and "Your closing is on [Weekday, Mon D]."
// 🔑 THE STAGE PAGES (dashboard stages spec, Jesse/Hannah 2026-11-03), wording from the spec exactly:
//   Offer · requested — "We’re preparing Doorly’s offer on [street]" / "We’ll send it to the seller’s agent shortly …"
//                    ("shortly": no 1-business-day SLA is enforced) / "Message your Doorly team" (no offer view in Homes).
//   Offer · sent     — "Doorly’s offer on [street] has been sent" / "It’s with the seller’s agent. …" / "Message your …".
//   Offer · accepted — the hero's own card (HeroOfferAccepted in pages/Dashboard: it owns the review overlay).
//   Under contract / Closing — the hero button is the borrower's ACTUAL next to-do (B1): `nextTodo` { label, targetId }
//                    from the stage's checklist; it scrolls to that item. None when nothing waits on the borrower.
//   Keys in hand     — "Congratulations, your home is yours!" / "Your closing documents are in Documents." / "View your
//                    closing documents"; the first-payment line only if servicing data exists (it does not today —
//                    journey.first_payment is never set, so the line never shows).
// Buttons are normal size everywhere (B4). Renders nothing at any other stage.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { journeyStepFor, JOURNEY_COPY, showOptionEnds, DASHBOARD_GREETING, headlineWithName, streetOf,
  DASHBOARD_NEW_WORDING_APPROVED, UNAPPROVED_COPY, DASHBOARD_ROUND2_WORDING_APPROVED, ROUND2_COPY } from '../../constants/journeySteps';
import { PrimaryButton, goToItem } from './StageParts';

const TITLE = { fontSize: 16, fontWeight: 800, color: '#000' };
const LINE = { fontSize: 13.5, color: '#000', marginTop: 4, lineHeight: 1.6 };
const ASK_BOX = { backgroundColor: '#eff3fe', border: '1px solid #bfdbfe' };   // components/guided/Step5AskCard ASK_BOX
// 🔑 Normal size, never full width (spec B4) — was 14px/18px, 15px, full width on a phone.
const PRIMARY = { padding: '10px 18px', fontSize: 14, fontWeight: 700, borderRadius: 10, border: 'none', background: '#3a6ad9', color: '#fff', cursor: 'pointer' };

export function MainCardFrame({ children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 sm:px-8 pt-8 pb-6">{children}</div>
    </div>
  );
}

export default function JourneyMainCard({ status, onBrowse, onViewSmartEstimate, onPrepareSmartEstimate = undefined, openError = null,
  greeting = DASHBOARD_GREETING, firstName = '', nextTodo = null, onMessage = undefined, onClosingDocuments = undefined }) {
  // 🔑 Greeting option 'c' (constants/journeySteps DASHBOARD_GREETING): the first name leads the headline. Off by default.
  const head = (h) => (greeting === 'c' ? headlineWithName(firstName, h) : h);
  const j = journeyStepFor(status);
  if (!j) return null;
  const stage = status.loan_stage;
  const facts = status.journey || {};

  if (stage === 'home_shopping') {
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage="home_shopping">
          {facts.approved_max ? <div data-testid="journey-main-title" style={TITLE}>{head(JOURNEY_COPY.approved(facts.approved_max))}</div> : null}
          <div data-testid="journey-main-line" style={LINE}>{JOURNEY_COPY.homeShoppingNext}</div>
          <div data-testid="journey-down-payment-panel" style={{ ...ASK_BOX, borderRadius: 10, padding: '10px 12px', marginTop: 12 }}>
            <div style={{ fontSize: 13.5, color: '#000', lineHeight: 1.6, overflowWrap: 'anywhere' }}>{JOURNEY_COPY.downPaymentReady}</div>
          </div>
          {onViewSmartEstimate && facts.smartestimate_document_id ? (
            <div style={{ marginTop: 12 }}>
              <button type="button" data-testid="journey-view-smartestimate" data-document-id={facts.smartestimate_document_id}
                // The link is minted on the way to the tap, as Documents' View does (utils/openDocument prepareDocument).
                onPointerEnter={() => onPrepareSmartEstimate && onPrepareSmartEstimate(facts.smartestimate_document_id)}
                onFocus={() => onPrepareSmartEstimate && onPrepareSmartEstimate(facts.smartestimate_document_id)}
                onTouchStart={() => onPrepareSmartEstimate && onPrepareSmartEstimate(facts.smartestimate_document_id)}
                onClick={() => onViewSmartEstimate(facts.smartestimate_document_id)}
                style={{ padding: 0, background: 'none', border: 'none', color: '#3a6ad9', fontSize: 13.5, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                {JOURNEY_COPY.viewSmartEstimate}
              </button>
              {openError ? <div data-testid="journey-view-smartestimate-error" role="alert" style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>{openError}</div> : null}
            </div>
          ) : null}
          <button type="button" data-testid="journey-browse-homes" onClick={onBrowse} className="guided-primary" style={{ ...PRIMARY, marginTop: 16 }}>
            {JOURNEY_COPY.browse}
          </button>
        </div>
      </MainCardFrame>
    );
  }

  const street = streetOf(facts.property_address);
  const todoButton = nextTodo && nextTodo.label ? (
    <div style={{ marginTop: 16 }}>
      <PrimaryButton testId="journey-next-todo" data-target={nextTodo.targetId} onClick={() => (nextTodo.onClick ? nextTodo.onClick() : goToItem(nextTodo.targetId))}>
        {nextTodo.label}
      </PrimaryButton>
    </div>
  ) : null;

  if (stage === 'offer_requested' || stage === 'offer_submitted') {
    const requested = stage === 'offer_requested';
    const title = requested ? JOURNEY_COPY.offerRequested(street)
      : (JOURNEY_COPY.offerSent(street) || (DASHBOARD_NEW_WORDING_APPROVED ? UNAPPROVED_COPY.offerSentNoAddress : ''));
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage={stage}>
          {title ? <div data-testid="journey-main-title" style={TITLE}>{head(title)}</div> : null}
          <div data-testid="journey-main-line" style={LINE}>{requested ? JOURNEY_COPY.offerRequestedLine : JOURNEY_COPY.offerSentLine}</div>
          <div style={{ marginTop: 16 }}>
            <PrimaryButton testId="journey-message-team" onClick={onMessage}>{JOURNEY_COPY.messageTeam}</PrimaryButton>
          </div>
        </div>
      </MainCardFrame>
    );
  }

  if (j.step === 3) {
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage={stage}>
          <div data-testid="journey-main-title" style={TITLE}>{head(JOURNEY_COPY.underContract(facts.property_address, facts.contract_price))}</div>
          {/* 🔑 Round 2 (2026-11-04): the option-period date is the borrower's B-C contract's and is shown ONLY once every
              borrower has signed it (the server sends none before — backend utils/bcContract). Before the contract is out to
              sign: "We’re preparing your purchase contract. …" and NO button (new wording, off until approved). */}
          {showOptionEnds(status) && (
            <div data-testid="journey-option-ends" style={LINE}>{JOURNEY_COPY.optionEnds(facts.option_period_end)}</div>
          )}
          {DASHBOARD_ROUND2_WORDING_APPROVED && !facts.bc_psa_signed && !facts.bc_psa_status && stage === 'option_period' && (
            <div data-testid="journey-preparing-contract" style={LINE}>{ROUND2_COPY.preparingContract}</div>
          )}
          {todoButton}
        </div>
      </MainCardFrame>
    );
  }

  if (j.step === 4) {
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage={stage}>
          <div data-testid="journey-main-title" style={TITLE}>{head(JOURNEY_COPY.closingTitle(facts.property_address, facts.contract_price))}</div>
          {facts.closing_date && <div data-testid="journey-closing-on" style={LINE}>{JOURNEY_COPY.closingOn(facts.closing_date)}</div>}
          {todoButton}
        </div>
      </MainCardFrame>
    );
  }

  if (j.step === 5) {
    // Servicing data: none exists today. Future source: Doorly's own servicing system for Doorly-originated notes during the
    // 30–90 day hold before sale (backlog, replacing Ziggi) — it must provide the amount, the due date and how to pay.
    const fp = facts.first_payment;
    return (
      <MainCardFrame testId="journey-main-card">
        <div data-stage={stage}>
          <div data-testid="journey-main-title" style={TITLE}>{JOURNEY_COPY.keysHeadline}</div>
          <div data-testid="journey-main-line" style={LINE}>{JOURNEY_COPY.keysLine}</div>
          {fp && fp.amount && fp.due_date ? (
            <div data-testid="journey-first-payment" style={LINE}>{JOURNEY_COPY.firstPayment(fp.amount, fp.due_date)}</div>
          ) : null}
          <div style={{ marginTop: 16 }}>
            <PrimaryButton testId="journey-closing-documents" onClick={onClosingDocuments}>{JOURNEY_COPY.keysButton}</PrimaryButton>
          </div>
        </div>
      </MainCardFrame>
    );
  }
  return null;
}
